// Orchestrator: diff two Slate documents into annotated render trees (RES-561).
//
// Two-sided annotation, no merged tree (plan decision D1): each pane renders a
// faithful clone of its own version — the left clone gains diffRemoved marks, the
// right clone diffAdded marks, and a changed passage is a paired removal+addition
// sharing one hunk id.

import alignBlocks from './alignBlocks';
import diffText from './diffText';
import diffTable from './diffTable';
import { cloneNodes, isSkipDiff, isIgnored, normalisedEqual } from './normalise';
import { createDiffContext, markSubtree } from './annotate';

const LIST_TYPES = ['bulleted-list', 'numbered-list'];

function isTextualType(type) {
	return type === undefined || type === 'text' || type === 'quote' || type === 'code'
		|| type === 'list-item' || (typeof type === 'string' && type.startsWith('heading'));
}

function allLeafChildren(node) {
	return Array.isArray(node.children) && node.children.every((c) => c && typeof c.text === 'string' && c.type === undefined);
}

// Last-resort comparison for blocks no specific differ handles (inline elements in
// text, side-by-sides, voids, oversized blocks): equal → untouched, different →
// whole block marked on both sides as one change.
function wholeBlockCompare(leftNode, rightNode, ctx) {
	if (normalisedEqual(leftNode, rightNode))
		return false;
	const hunkId = ctx.newHunk('changed');
	markSubtree(leftNode, 'diffRemoved', hunkId, 'changed');
	markSubtree(rightNode, 'diffAdded', hunkId, 'changed');
	return true;
}

// Dispatch a modified pair by block type. Annotates in place; returns changed.
function diffBlockPair(leftNode, rightNode, ctx) {
	// A bare leaf in a block list (pathological pasted content) has no children to
	// recurse into — compare it wholesale rather than silently reporting unchanged.
	if (typeof leftNode.text === 'string' || typeof rightNode.text === 'string')
		return wholeBlockCompare(leftNode, rightNode, ctx);
	const type = leftNode.type || 'text';
	if (isTextualType(leftNode.type)) {
		// list-items (and pasted content) can nest elements between leaves — only
		// the pure-leaf case is safely word-diffable.
		if (type !== 'list-item' || (allLeafChildren(leftNode) && allLeafChildren(rightNode))) {
			const dt = diffText(leftNode.children, rightNode.children, ctx);
			if (dt) {
				leftNode.children = dt.leftChildren;
				rightNode.children = dt.rightChildren;
				return dt.changed;
			}
		}
		if (type === 'list-item' && !allLeafChildren(leftNode) && !allLeafChildren(rightNode)
			&& Array.isArray(leftNode.children) && Array.isArray(rightNode.children)
			&& leftNode.children.every((c) => c && c.type !== undefined)
			&& rightNode.children.every((c) => c && c.type !== undefined)) {
			// Nested-list item whose children are all elements — recurse.
			const res = diffBlockList(leftNode.children, rightNode.children, ctx);
			return res.changed;
		}
		return wholeBlockCompare(leftNode, rightNode, ctx);
	}
	if (LIST_TYPES.includes(type)) {
		const res = diffBlockList(leftNode.children, rightNode.children, ctx);
		return res.changed;
	}
	if (type === 'table-wrapper')
		return diffTable(leftNode, rightNode, ctx, diffBlockList);
	// image-wrapper, parameter, parameter-component, side-by-side, unknown types:
	// signature/props comparison only — never worth a partial annotation.
	return wholeBlockCompare(leftNode, rightNode, ctx);
}

// Diff two block lists in place (both must be clones). Returns per-side statuses
// ('unchanged' | 'changed' | 'removed' | 'added') parallel to the input arrays,
// plus an overall changed flag. Reused for top-level content, list children and
// table-cell contents.
export function diffBlockList(leftBlocks, rightBlocks, ctx) {
	const leftStatuses = new Array(leftBlocks.length).fill('unchanged');
	const rightStatuses = new Array(rightBlocks.length).fill('unchanged');
	let changed = false;
	alignBlocks(leftBlocks, rightBlocks).forEach((op) => {
		if (op.kind === 'unchanged')
			return;
		if (op.kind === 'removed') {
			const node = leftBlocks[op.left];
			if (isSkipDiff(node) || isIgnored(node))
				return;
			markSubtree(node, 'diffRemoved', ctx.newHunk('removed'));
			leftStatuses[op.left] = 'removed';
			changed = true;
		} else if (op.kind === 'added') {
			const node = rightBlocks[op.right];
			if (isSkipDiff(node) || isIgnored(node))
				return;
			markSubtree(node, 'diffAdded', ctx.newHunk('added'));
			rightStatuses[op.right] = 'added';
			changed = true;
		} else {
			const l = leftBlocks[op.left];
			const r = rightBlocks[op.right];
			if (isSkipDiff(l) || isSkipDiff(r) || isIgnored(l) || isIgnored(r))
				return;
			if (diffBlockPair(l, r, ctx)) {
				leftStatuses[op.left] = 'changed';
				rightStatuses[op.right] = 'changed';
				changed = true;
			}
		}
	});
	return { leftStatuses, rightStatuses, changed };
}

// Main entry point. Inputs are never mutated; the returned trees are annotated
// clones that keep every render-critical prop (columnWidths, styles, pageBreaks…).
// Pass a shared ctx (createDiffContext) to keep hunk ids unique across several
// diffSlate runs (one per content key).
export default function diffSlate(leftNodes, rightNodes, ctx) {
	ctx = ctx || createDiffContext();
	const leftAnnotated = cloneNodes(leftNodes || []);
	const rightAnnotated = cloneNodes(rightNodes || []);
	const { leftStatuses, rightStatuses, changed } = diffBlockList(leftAnnotated, rightAnnotated, ctx);
	return {
		leftAnnotated,
		rightAnnotated,
		blockStatuses: { left: leftStatuses, right: rightStatuses },
		hunks: ctx.hunks,
		changed,
	};
}
